import { ahuBoardApi } from './ahuBoardApi';
import type { ICatalogRow } from './ahuBoardFormsApi';

/**
 * Phase 6e2a — endpoints owned by the COMPONENT FORM layer (Part 6 §49/§50).
 * Injected into the 6a `ahuBoardApi` (one reducerPath, shared baseQuery).
 *
 * §20 rows: #47 (GET/PATCH ahu/<plural>/<id> — the auto-save write),
 * #49/#50 (fan/coil/phx aux), per-type CMS params docs
 * (cms/1/v1.0.0/ahu/<plural>/params/), catalogue master lists + counts
 * (§49.4/§49.5: pages = <sourceURL>?page=N&<filters>; count =
 * <sourceURL>count/?<filters> — counter.js getBaseURL appends "/count/" and
 * collapses the double slash).
 *
 * PATCH bodies are built by the §49.2 auto-save engine (autoSaveModel.ts) —
 * this file is pure HTTP. No rollback on error anywhere (§49.2 error path).
 */

/** Loose master/CMS row (selects read id + name/value/spn/label). */
export type IMasterRow = ICatalogRow;

/** Per-type CMS params doc (componentCms — arbitrary option-list dictionary). */
export type IComponentCmsDoc = Record<string, unknown>;

const toRows = (res: unknown): IMasterRow[] => {
  if (Array.isArray(res)) return res as IMasterRow[];
  const obj = res as { results?: IMasterRow[] } | null;
  return obj?.results ?? [];
};

const toCount = (res: unknown): number => {
  if (typeof res === 'number') return res;
  if (typeof res === 'string') return Number(res) || 0;
  const obj = res as { count?: number } | number[] | null;
  if (Array.isArray(obj)) return Number(obj[0]) || 0;
  return Number(obj?.count) || 0;
};

export const ahuComponentFormsApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    // =====================================================================
    // §20.5 #47 — concrete component detail (the form model's GET side)
    // =====================================================================
    getComponentDetail: builder.query<
      Record<string, unknown>,
      { plural: string; id: number }
    >({
      query: ({ plural, id }) => `/api/v1.0/ahu/${plural}/${id}`,
    }),

    // Per-type CMS params doc — legacy fn['current.<partName>.cms']
    // (component.<type>.model.cms urlRoot = cms/1/v1.0.0/ahu/<plural>/params/
    // — except evap_pack whose route family is SINGULAR; the chassis passes
    // the full URL so per-type overrides stay in config).
    getComponentCmsDoc: builder.query<IComponentCmsDoc, string>({
      query: (url) => url,
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // =====================================================================
    // §49.4/§49.5 — catalogue master stack (CMSPages + counter)
    // =====================================================================
    // One CMS page (cms.components.js): url = <sourceURL>?page=N&<filters>.
    getCatalogPage: builder.query<
      IMasterRow[],
      { sourceURL: string; page: number; filters: string }
    >({
      query: ({ sourceURL, page, filters }) =>
        `${sourceURL}?page=${page}${filters ? `&${filters}` : ''}`,
      transformResponse: toRows,
    }),

    // Filtered count (counter.js): <sourceURL>count/?<filters> ("/count/"
    // appended to the base, double slash collapsed — sourceURL ends in /).
    getCatalogCount: builder.query<number, { sourceURL: string; filters: string }>({
      query: ({ sourceURL, filters }) =>
        `${sourceURL}count/${filters ? `?${filters}` : ''}`,
      transformResponse: toCount,
    }),

    // getFirstModelDetailed — first row of the filtered list (legacy fetches
    // page 1 of the detailed URL and takes models[0]).
    getCatalogFirstDetailed: builder.query<
      IMasterRow | null,
      { sourceURL: string; filters: string }
    >({
      query: ({ sourceURL, filters }) =>
        `${sourceURL}?page=1${filters ? `&${filters}` : ''}`,
      transformResponse: (res: unknown) => toRows(res)[0] ?? null,
    }),

    // Generic master/CMS list by absolute URL (pricing methods, fan
    // mountings, select modes, silencer width/height/length ranges, energy
    // wheel speeds/flow types/fan locations, UDC collections — §50.1 wiring).
    getMasterListByUrl: builder.query<IMasterRow[], string>({
      query: (url) => url,
      transformResponse: toRows,
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // AdditionalLoadable (§49.5) — keyed side-list: GET baseUrl + filter;
    // URL-as-cache-key semantics are native to RTKQ (stateStorage
    // 'AdditionalLoadable' equivalent).
    getAdditionalLoadable: builder.query<
      IMasterRow[],
      { baseUrl: string; filters: string }
    >({
      query: ({ baseUrl, filters }) => `${baseUrl}${filters ? `?${filters}` : ''}`,
      transformResponse: toRows,
    }),

    // =====================================================================
    // §20.5 #49/#50 — per-type aux endpoints
    // =====================================================================
    // Coil duties collection (ahu/coils/<id>/duties/).
    getCoilDuties: builder.query<IMasterRow[], number>({
      query: (coilId) => `/api/v1.0/ahu/coils/${coilId}/duties/`,
      transformResponse: toRows,
    }),

    // RatedCoilModel (§50.1 coil row — THE G31 pipe feed source).
    getRatedCoil: builder.query<Record<string, unknown>, number>({
      query: (coilId) => `/api/v1.0/ahu/rating_mode_coils/${coilId}`,
    }),
    // Rating-mode entry PATCHes tmp_params first (§50.1 coil; STUB seam for
    // 6e2c — wired, fired only by the rating flow).
    patchRatedCoil: builder.mutation<
      Record<string, unknown>,
      { coilId: number; data: Record<string, unknown> }
    >({
      query: ({ coilId, data }) => ({
        url: `/api/v1.0/ahu/rating_mode_coils/${coilId}`,
        method: 'PATCH',
        body: data,
      }),
    }),

    // RatedPhxModel (§50.1 phx row).
    getRatedPhx: builder.query<Record<string, unknown>, number>({
      query: (phxId) => `/api/v1.0/ahu/plate_heat_exchangers/rating_mode/${phxId}`,
    }),
    patchRatedPhx: builder.mutation<
      Record<string, unknown>,
      { phxId: number; data: Record<string, unknown> }
    >({
      query: ({ phxId, data }) => ({
        url: `/api/v1.0/ahu/plate_heat_exchangers/rating_mode/${phxId}`,
        method: 'PATCH',
        body: data,
      }),
    }),

    // Fan loadParams engine (§50.1 fan row): GET
    // ahu/fans/params/<param>/?<filters> (mfg/type/series filters;
    // fan_-prefixed for redundancy/speed_condition). stateStorage
    // `parameters` URL-cache + pending-dedup come free from RTKQ.
    getFanParams: builder.query<
      Record<string, unknown>,
      { param: string; filters: string }
    >({
      query: ({ param, filters }) =>
        `/api/v1.0/ahu/fans/params/${param}/${filters ? `?${filters}` : ''}`,
    }),

    // Certifications multiCheckbox backing (§49.5 / §50.1 ewheel + phx).
    getTypeCertifications: builder.query<
      IMasterRow[],
      { plural: string; id: number }
    >({
      query: ({ plural, id }) => `/api/v1.0/ahu/${plural}/${id}/certifications/`,
      transformResponse: toRows,
    }),
    // Commit-on-close diff: create {id} with wait / destroy per row (§49.5).
    // ★ PUT <…>/certifications/<certId>, not POST to the list. Legacy's
    // `collection.create({id}, {wait:true})` (multi_checkbox_selector.js:176-184)
    // builds a model that already HAS an id, so Backbone's `isNew()` is false and
    // `save()` issues a PUT at the model URL — which is the only route Django
    // exposes for the write: the list route is `{'get': 'list'}` and the detail
    // route is `{'get','put':'create','delete':'destroy'}`
    // (rest_ahu/urls.py:382-389 energy wheels, :466-473 phx). The port's POST to
    // the list URL is a 405, so no certification ticked in the Data Bar was ever
    // saved. The body stays `{id}` — the viewset rejects a mismatch against the
    // URL's certification_id (`energy_wheel_viewset.py:375-381`).
    createTypeCertification: builder.mutation<
      unknown,
      { plural: string; id: number; certId: number }
    >({
      query: ({ plural, id, certId }) => ({
        url: `/api/v1.0/ahu/${plural}/${id}/certifications/${certId}`,
        method: 'PUT',
        body: { id: certId },
      }),
    }),
    deleteTypeCertification: builder.mutation<
      unknown,
      { plural: string; id: number; certId: number }
    >({
      query: ({ plural, id, certId }) => ({
        url: `/api/v1.0/ahu/${plural}/${id}/certifications/${certId}`,
        method: 'DELETE',
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetComponentDetailQuery,
  useGetComponentCmsDocQuery,
  useGetCatalogPageQuery,
  useGetCatalogCountQuery,
  useGetMasterListByUrlQuery,
  useGetAdditionalLoadableQuery,
  useGetCoilDutiesQuery,
  useGetRatedCoilQuery,
  usePatchRatedCoilMutation,
  useGetRatedPhxQuery,
  usePatchRatedPhxMutation,
  useGetFanParamsQuery,
  useGetTypeCertificationsQuery,
  useCreateTypeCertificationMutation,
  useDeleteTypeCertificationMutation,
} = ahuComponentFormsApi;
